import { useState } from 'react';
import { products, type Product } from '../../../mocks/tienda';
import { useSupabaseAdmin } from '../../../hooks/useSupabaseAdmin';
import { productToDb, productFromDb } from '../../../hooks/useSupabaseProducts';
import { productSlugUrl } from '../../../utils/slug';

const availabilityOptions = [
  { value: 'disponible', label: 'En stock' },
  { value: 'encargo', label: 'Bajo encargo' },
  { value: 'agotado', label: 'Agotado' },
];

const categoryOptions = [
  { value: 'alta-costura', label: 'Alta Costura' },
  { value: 'capsula', label: 'Cápsula' },
];

export default function AdminProducts() {
  const { data, add, update, remove, loading } = useSupabaseAdmin<Product>({
    table: 'products',
    initialData: products,
    mapToDb: productToDb,
    mapFromDb: productFromDb,
  });
  const [editing, setEditing] = useState<Product | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = data.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.collection.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = (formData: FormData) => {
    const product = {
      name: formData.get('name') as string,
      category: formData.get('category') as Product['category'],
      collection: formData.get('collection') as string,
      price: Number(formData.get('price')),
      description: formData.get('description') as string,
      details: (formData.get('details') as string).split('\n').filter(Boolean),
      materials: formData.get('materials') as string,
      measurements: formData.get('measurements') as string,
      image: formData.get('image') as string,
      images: (formData.get('images') as string).split('\n').filter(Boolean),
      availability: formData.get('availability') as Product['availability'],
      deliveryTime: formData.get('deliveryTime') as string,
    };

    if (editing) {
      update(editing.id, product);
      setEditing(null);
    } else {
      add(product);
      setIsAdding(false);
    }
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="flex items-center gap-2 text-brand-gray-500 text-sm">
          <i className="ri-loader-4-line animate-spin" />
          Sincronizando con la nube...
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="font-serif text-brand-black text-2xl font-medium">
          Productos <span className="text-brand-gray-400">({data.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Buscar producto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 bg-white border border-brand-gray-200 rounded-lg px-4 py-2.5 font-sans text-sm focus:outline-none focus:border-brand-black"
          />
          <button
            onClick={() => { setIsAdding(true); setEditing(null); }}
            className="bg-brand-black text-white font-sans text-sm px-5 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap"
          >
            + Nuevo
          </button>
        </div>
      </div>

      {(isAdding || editing) && (
        <form
          onSubmit={(e) => { e.preventDefault(); handleSave(new FormData(e.currentTarget)); }}
          className="bg-white border border-brand-gray-200 rounded-2xl p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-brand-black text-lg">
              {editing ? 'Editar Producto' : 'Nuevo Producto'}
            </h3>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="w-8 h-8 flex items-center justify-center text-brand-gray-400 hover:text-brand-black"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Nombre *</label>
              <input name="name" defaultValue={editing?.name ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Colección *</label>
              <input name="collection" defaultValue={editing?.collection ?? ''} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Precio (€) *</label>
              <input name="price" type="number" defaultValue={editing?.price ?? 0} required className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Stock</label>
                <input name="stock" type="number" min={0} defaultValue={editing?.stock ?? 0} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Categoría</label>
                <select name="category" defaultValue={editing?.category ?? 'capsula'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black bg-white">
                  {categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Disponibilidad</label>
                <select name="availability" defaultValue={editing?.availability ?? 'disponible'} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black bg-white">
                  {availabilityOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Descripción</label>
            <textarea name="description" defaultValue={editing?.description ?? ''} rows={3} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Detalles (uno por línea)</label>
              <textarea name="details" defaultValue={editing?.details.join('\n') ?? ''} rows={4} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
            </div>
            <div className="space-y-4">
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Materiales</label>
                <input name="materials" defaultValue={editing?.materials ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Medidas</label>
                <input name="measurements" defaultValue={editing?.measurements ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
              <div>
                <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Tiempo de entrega</label>
                <input name="deliveryTime" defaultValue={editing?.deliveryTime ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Imagen principal (URL)</label>
              <input name="image" defaultValue={editing?.image ?? ''} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black" />
            </div>
            <div>
              <label className="font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium">Galería (una URL por línea)</label>
              <textarea name="images" defaultValue={editing?.images.join('\n') ?? ''} rows={3} className="mt-1 w-full border border-brand-gray-200 rounded-lg px-3 py-2 font-sans text-sm focus:outline-none focus:border-brand-black resize-none" />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button type="submit" className="bg-brand-black text-white font-sans text-sm px-6 py-2.5 rounded-lg hover:bg-brand-gray-600 transition-colors whitespace-nowrap">
              Guardar Producto
            </button>
            <button
              type="button"
              onClick={() => { setEditing(null); setIsAdding(false); }}
              className="border border-brand-gray-200 text-brand-black font-sans text-sm px-6 py-2.5 rounded-lg hover:border-brand-black transition-colors whitespace-nowrap"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="bg-white border border-brand-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-brand-gray-50">
              <tr>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Producto</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Colección</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Precio</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Estado</th>
                <th className="text-left font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Stock</th>
                <th className="text-right font-sans text-brand-gray-600 text-xs uppercase tracking-wider font-medium px-5 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-gray-200">
              {filtered.map((product) => (
                <tr key={product.id} className="hover:bg-brand-gray-50/50 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-brand-gray-100 flex-shrink-0">
                        {product.image ? (
                          <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-brand-gray-400 text-xs">Sin img</div>
                        )}
                      </div>
                      <div>
                        <p className="font-sans text-brand-black text-sm font-medium">{product.name}</p>
                        <p className="font-sans text-brand-gray-400 text-xs capitalize">{product.category.replace('-', ' ')}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-sans text-brand-gray-600 text-sm">{product.collection}</td>
                  <td className="px-5 py-4 font-sans text-brand-black text-sm font-medium">
                    {product.price.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 })}
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full font-sans text-[11px] uppercase tracking-wider font-medium ${
                      product.availability === 'disponible' ? 'bg-brand-sage text-brand-black' :
                      product.availability === 'encargo' ? 'bg-brand-black text-white' :
                      'bg-brand-gray-200 text-brand-gray-500'
                    }`}>
                      {availabilityOptions.find((o) => o.value === product.availability)?.label}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`font-sans text-sm font-medium ${
                      (product.stock ?? 0) === 0 ? 'text-red-500' :
                      (product.stock ?? 0) <= 3 ? 'text-amber-600' :
                      'text-brand-black'
                    }`}>
                      {product.stock ?? 0}
                    </span>
                    {(product.stock ?? 0) <= 3 && (product.stock ?? 0) > 0 && (
                      <span className="block font-sans text-[10px] text-amber-600 mt-0.5">Bajo stock</span>
                    )}
                    {(product.stock ?? 0) === 0 && (
                      <span className="block font-sans text-[10px] text-red-500 mt-0.5">Sin stock</span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <a
                        href={productSlugUrl(product)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Ver como cliente"
                      >
                        <i className="ri-eye-line" />
                      </a>
                      <button
                        onClick={() => { setEditing(product); setIsAdding(false); }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-brand-black hover:text-white transition-colors"
                        title="Editar"
                      >
                        <i className="ri-pencil-line" />
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(`¿Eliminar "${product.name}"?`)) remove(product.id);
                        }}
                        className="w-8 h-8 rounded-lg bg-brand-gray-100 flex items-center justify-center text-brand-gray-600 hover:bg-red-600 hover:text-white transition-colors"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-10 text-center">
            <p className="font-sans text-brand-gray-400 text-sm">No se encontraron productos</p>
          </div>
        )}
      </div>
    </div>
  );
}